import React, { useEffect, useMemo, useState } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import axios from 'axios';
import { canAccess } from '../utils/canAccess';
import TransfertGasoilModal from '../components/TransfertGasoilModal';
import { getDefaultDates } from '../utils/dateUtils';
import Select from 'react-select';
import KpiCard from '../components/KpiCard';
import {
  Modal, Button, Form, Row, Col, Card,
  Alert, Badge, Spinner, Dropdown, InputGroup
} from 'react-bootstrap';
import * as XLSX from 'xlsx';
import {
  FaCalendarAlt, FaEdit, FaTrashAlt, FaPlus,
  FaTruck, FaBuilding, FaClock, FaGasPump,
  FaSearch, FaSyncAlt, FaBoxes, FaExchangeAlt,
  FaEllipsisV, FaBroom, FaFileExcel, FaFileCsv,
  FaWrench, FaFilePdf
} from 'react-icons/fa';
import '../styles/pointageMaterielListe.css';

// kept for modal selects
const COLORS = {
  bg: '#f5f5db',
  brown: '#6d4e36',
  brownLight: '#8b5e3c',
  cream: '#fcfbf7',
  border: '#d8c9b8'
};

const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 42,
    borderRadius: 10,
    borderColor: state.isFocused ? COLORS.brownLight : COLORS.border,
    boxShadow: state.isFocused ? '0 0 0 0.15rem rgba(109, 78, 54, 0.12)' : 'none',
    ':hover': { borderColor: COLORS.brownLight },
    backgroundColor: '#fff'
  }),
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
  menu: (base) => ({ ...base, zIndex: 99999, borderRadius: 10, overflow: 'hidden' }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? COLORS.brown : state.isFocused ? COLORS.bg : '#fff',
    color: state.isSelected ? '#fff' : COLORS.brown,
    cursor: 'pointer'
  }),
  placeholder: (base) => ({ ...base, color: '#8b6f58' }),
  singleValue: (base) => ({ ...base, color: COLORS.brown, fontWeight: 500 })
};

const toNumberOrNull = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(String(v).replace(',', '.'));
  return Number.isNaN(n) ? null : n;
};

const normalizeText = (s = '') =>
  String(s).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();

const PAGE_SIZE = 50;

const PointageMaterielListePage = ({ user }) => {
  const { startDate: defaultStartDate, endDate: defaultEndDate } = getDefaultDates();

  // ── State ──
  const [loading, setLoading]               = useState(true);
  const [saving, setSaving]                 = useState(false);
  const [startDate, setStartDate]           = useState(defaultStartDate);
  const [endDate, setEndDate]               = useState(defaultEndDate);
  const [pointages, setPointages]           = useState([]);
  const [materiels, setMateriels]           = useState([]);
  const [chantiers, setChantiers]           = useState([]);
  const [allChantiers, setAllChantiers]     = useState([]);
  const [citernes, setCiternes]             = useState([]);
  const [selectedChantiers, setSelectedChantiers]   = useState([]);
  const [selectedMateriels, setSelectedMateriels]   = useState([]);
  const [showAddModal, setShowAddModal]     = useState(false);
  const [showEditModal, setShowEditModal]   = useState(false);
  const [selectedPointage, setSelectedPointage] = useState(null);
  const [errorMsg, setErrorMsg]             = useState('');
  const [successMsg, setSuccessMsg]         = useState('');
  const [showTransferModal, setShowTransferModal] = useState(false);

  // sort / search / paginate
  const [searchText, setSearchText]   = useState('');
  const [sortField, setSortField]     = useState('date');
  const [sortDir, setSortDir]         = useState('desc');
  const [page, setPage]               = useState(1);

  const initialFormData = useMemo(() => ({
    id_materiel: '', chantier_id: '', date: '', meteo: '',
    compteur_prec: '', compteur_nv: '', heures_reels: '', qte: '',
    gasoil: '', essence: '', citerne_id: '', gasoil_l: '',
    graisse: '', vidange: '', reparation: '', piece_de_rechange: '', observation: ''
  }), []);

  const [formData, setFormData] = useState(initialFormData);

  // ── Helpers ──
  const resetMessages = () => { setErrorMsg(''); setSuccessMsg(''); };
  const canAdd = !!user?.role;

  // ── Derived options ──
  const chantierNameById = useMemo(() => {
    const m = new Map();
    (chantiers || []).forEach(c => { if (c?.id != null) m.set(Number(c.id), c?.name || ''); });
    return m;
  }, [chantiers]);

  const chantierOptions = useMemo(() =>
    (chantiers || [])
      .filter(c => c?.id != null && c?.name)
      .map(c => ({ value: Number(c.id), label: c.name }))
      .sort((a, b) => a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' })),
    [chantiers]);

  const allChantierOptions = useMemo(() => {
    const source = allChantiers.length ? allChantiers : chantiers;
    return (source || [])
      .filter(c => c?.id != null && c?.name)
      .map(c => ({ value: Number(c.id), label: c.name }))
      .sort((a, b) => a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' }));
  }, [allChantiers, chantiers]);

  const materielOptions = useMemo(() =>
    (materiels || [])
      .filter(m => m?.id != null && (m.etat || '').toLowerCase().trim() !== 'bloque')
      .map(m => ({
        value: Number(m.id),
        label: `#${m.id} — ${m.matricule || 'Sans matricule'}${m.type ? ` — ${m.type}` : ''}${m.chantier_actuel ? ` (${m.chantier_actuel})` : ''}`
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' })),
    [materiels]);

  const selectedFormChantier = allChantierOptions.find(o => String(o.value) === String(formData.chantier_id)) || null;
  const selectedFormMateriel = materielOptions.find(o => String(o.value) === String(formData.id_materiel)) || null;

  const citerneOptions = useMemo(() =>
    (citernes || [])
      .filter(c => c?.id && c?.nom)
      .map(c => ({ value: Number(c.id), label: `${c.nom} (Stock: ${Number(c.stock_reel_l || 0)} L)` }))
      .sort((a, b) => a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' })),
    [citernes]);

  const selectedFormCiterne = citerneOptions.find(o => String(o.value) === String(formData.citerne_id)) || null;

  const filterMaterielOptions = useMemo(() =>
    (materiels || [])
      .filter(m => m?.id != null)
      .map(m => ({
        value: Number(m.id),
        label: `#${m.id} — ${m.matricule || 'Sans matricule'}${m.type ? ` — ${m.type}` : ''}`
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' })),
    [materiels]);

  // ── Fetch ──
  const fetchData = async () => {
    try {
      setLoading(true);
      resetMessages();
      let url = '/pointage_materiels';
      if (startDate && endDate) url = `/pointage_materiels?start=${startDate}&end=${endDate}`;
      const [resP, resM, resC, resCit] = await Promise.all([
        axios.get(url),
        axios.get('/materiels'),
        axios.get('/chantiers'),
        axios.get('/citernes-gasoil')
      ]);
      setPointages(Array.isArray(resP.data) ? resP.data : []);
      setMateriels(Array.isArray(resM.data) ? resM.data : []);
      setChantiers(Array.isArray(resC.data) ? resC.data : []);
      setCiternes(Array.isArray(resCit.data) ? resCit.data : []);
    } catch (err) {
      console.error(err);
      setErrorMsg('❌ Erreur de chargement des pointages matériels.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []); // eslint-disable-line
  useEffect(() => { if (startDate && endDate) fetchData(); }, [startDate, endDate]); // eslint-disable-line
  useEffect(() => { setPage(1); }, [searchText, selectedChantiers, selectedMateriels]);

  // ── Sort / filter / paginate ──
  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
    setPage(1);
  };

  const SortIcon = ({ field }) => (
    <span style={{ opacity: sortField === field ? 1 : 0.22, marginLeft: 3, fontSize: '0.68em' }}>
      {sortField === field ? (sortDir === 'asc' ? '▲' : '▼') : '▲'}
    </span>
  );

  const TH = ({ field, children, style = {}, noSort = false }) => (
    <th
      onClick={() => !noSort && field && toggleSort(field)}
      className={noSort ? 'no-sort' : ''}
      style={style}
    >
      {children}
      {!noSort && field && <SortIcon field={field} />}
    </th>
  );

  const getChantierName = (row) =>
    row?.chantier?.name || row?.Chantier?.name || row?.chantier_name ||
    chantierNameById.get(Number(row?.chantier_id || 0)) || '';

  const filteredPointages = useMemo(() => {
    let result = pointages;

    if (selectedChantiers.length) {
      const ids = new Set(selectedChantiers.map(x => Number(x.value)));
      result = result.filter(p => {
        const id = Number(p?.chantier_id || 0) || Number(p?.chantier?.id || 0) || Number(p?.Chantier?.id || 0) || 0;
        return id && ids.has(id);
      });
    }

    if (selectedMateriels.length) {
      const ids = new Set(selectedMateriels.map(x => Number(x.value)));
      result = result.filter(p => ids.has(Number(p?.id_materiel || 0)));
    }

    if (searchText.trim()) {
      const txt = normalizeText(searchText);
      result = result.filter(p => {
        const chantierName = p?.chantier?.name || p?.Chantier?.name || chantierNameById.get(Number(p?.chantier_id || 0)) || '';
        const hay = normalizeText([
          p.id, p.date, chantierName,
          p.Materiel?.matricule, p.Materiel?.type, p.Materiel?.chantier_actuel,
          p.heures_reels, p.gasoil, p.essence, p.observation, p.meteo
        ].filter(v => v != null).join(' '));
        return hay.includes(txt);
      });
    }

    return result;
  }, [pointages, selectedChantiers, selectedMateriels, searchText, chantierNameById]);

  const sortedPointages = useMemo(() => {
    const arr = [...filteredPointages];
    arr.sort((a, b) => {
      let va, vb;
      switch (sortField) {
        case 'chantier':
          va = a?.chantier?.name || a?.Chantier?.name || a?.chantier_name || '';
          vb = b?.chantier?.name || b?.Chantier?.name || b?.chantier_name || '';
          break;
        case 'matricule': va = a.Materiel?.matricule || ''; vb = b.Materiel?.matricule || ''; break;
        case 'type':      va = a.Materiel?.type || '';      vb = b.Materiel?.type || '';      break;
        default:          va = a[sortField] ?? '';          vb = b[sortField] ?? '';
      }
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ?  1 : -1;
      return 0;
    });
    return arr;
  }, [filteredPointages, sortField, sortDir]);

  const totalPages    = Math.max(1, Math.ceil(sortedPointages.length / PAGE_SIZE));
  const pagedPointages = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return sortedPointages.slice(start, start + PAGE_SIZE);
  }, [sortedPointages, page]);

  const kpi = useMemo(() => ({
    total:          filteredPointages.length,
    totalHeures:    filteredPointages.reduce((s, r) => s + (Number(r?.heures_reels || 0) || 0), 0),
    totalGasoil:    filteredPointages.reduce((s, r) => s + (Number(r?.gasoil || 0) || 0), 0),
    chantiersCount: selectedChantiers.length
  }), [filteredPointages, selectedChantiers]);

  // ── Form handlers ──
  const resetForm = () => setFormData(initialFormData);

  const openAddModal = async () => {
    resetForm(); resetMessages(); setSelectedPointage(null);
    setFormData(prev => ({ ...prev, date: startDate || '' }));
    try {
      const res = await axios.get('/chantiers?all=true');
      setAllChantiers(Array.isArray(res.data) ? res.data : []);
    } catch { setAllChantiers([]); }
    setShowAddModal(true);
  };

  const openEditModal = async (row) => {
    resetMessages();
    setSelectedPointage(row);
    setFormData({
      id_materiel:       row.id_materiel ?? '',
      chantier_id:       row.chantier_id ?? row.chantier?.id ?? row.Chantier?.id ?? '',
      date:              row.date ?? '',
      meteo:             row.meteo ?? '',
      compteur_prec:     row.compteur_prec ?? '',
      compteur_nv:       row.compteur_nv ?? '',
      heures_reels:      row.heures_reels ?? '',
      qte:               row.qte ?? '',
      gasoil:            row.gasoil ?? '',
      essence:           row.essence ?? '',
      citerne_id:        row.citerne_id ?? '',
      gasoil_l:          row.gasoil_l ?? '',
      graisse:           row.graisse ?? '',
      vidange:           row.vidange ?? '',
      reparation:        row.reparation ?? '',
      piece_de_rechange: row.piece_de_rechange ?? '',
      observation:       row.observation ?? ''
    });
    try {
      const res = await axios.get('/chantiers?all=true');
      setAllChantiers(Array.isArray(res.data) ? res.data : []);
    } catch { setAllChantiers([]); }
    setShowEditModal(true);
  };

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const validate = () => {
    const heures = toNumberOrNull(formData.heures_reels) ?? 0;
    const qte    = toNumberOrNull(formData.qte) ?? 0;
    if (!formData.id_materiel)  return '❌ Veuillez sélectionner un matériel.';
    if (!formData.chantier_id)  return '❌ Veuillez sélectionner un chantier.';
    if (!formData.date)         return '❌ Veuillez sélectionner une date.';
    if (heures < 0 || qte < 0) return '❌ heures_reels et qte doivent être ≥ 0.';
    if (heures === 0 && qte === 0) return '❌ heures_reels et qte ne peuvent pas être tous les deux à 0.';
    const cp = toNumberOrNull(formData.compteur_prec);
    const cn = toNumberOrNull(formData.compteur_nv);
    if (cp !== null && cn !== null && cn < cp) return '❌ compteur_nv ne peut pas être inférieur à compteur_prec.';
    return null;
  };

  const buildPayload = () => ({
    id_materiel:       Number(formData.id_materiel),
    chantier_id:       Number(formData.chantier_id),
    date:              formData.date,
    meteo:             formData.meteo || null,
    compteur_prec:     toNumberOrNull(formData.compteur_prec),
    compteur_nv:       toNumberOrNull(formData.compteur_nv),
    heures_reels:      toNumberOrNull(formData.heures_reels) ?? 0,
    qte:               toNumberOrNull(formData.qte) ?? 0,
    gasoil:            toNumberOrNull(formData.gasoil),
    essence:           toNumberOrNull(formData.essence),
    citerne_id:        formData.citerne_id ? Number(formData.citerne_id) : null,
    gasoil_l:          toNumberOrNull(formData.gasoil_l) ?? 0,
    graisse:           formData.graisse || null,
    vidange:           formData.vidange || null,
    reparation:        formData.reparation || null,
    piece_de_rechange: formData.piece_de_rechange || null,
    observation:       formData.observation || null
  });

  const handleSubmit = async () => {
    const err = validate();
    if (err) { setErrorMsg(err); return; }
    try {
      setSaving(true); resetMessages();
      await axios.post('/pointage_materiels', buildPayload());
      setShowAddModal(false);
      setSuccessMsg('✅ Pointage matériel ajouté avec succès.');
      fetchData();
    } catch (e) {
      console.error(e);
      setErrorMsg(e.response?.data?.message || "❌ Erreur lors de l'enregistrement.");
    } finally { setSaving(false); }
  };

  const handleUpdate = async () => {
    if (!selectedPointage?.id) return;
    const err = validate();
    if (err) { setErrorMsg(err); return; }
    try {
      setSaving(true); resetMessages();
      await axios.put(`/pointage_materiels/${selectedPointage.id}`, buildPayload());
      setShowEditModal(false);
      setSelectedPointage(null);
      setSuccessMsg('✅ Pointage matériel modifié avec succès.');
      fetchData();
    } catch (e) {
      console.error(e);
      setErrorMsg(e.response?.data?.message || '❌ Erreur lors de la mise à jour.');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!canAccess(user, 'pointage-materiels:delete')) return;
    if (window.confirm('Supprimer ce pointage matériel ?')) {
      try {
        resetMessages();
        await axios.delete(`/pointage_materiels/${id}`);
        setSuccessMsg('✅ Pointage matériel supprimé.');
        fetchData();
      } catch (e) {
        console.error(e);
        setErrorMsg('❌ Erreur suppression.');
      }
    }
  };

  // ── Export ──
  const handleExportExcel = () => {
    const rows = filteredPointages.map(row => {
      const chantierId = Number(row?.chantier_id || 0) || Number(row?.chantier?.id || 0) || null;
      const chantierName = getChantierName(row);
      return {
        ID: row.id, Date: row.date, Chantier_ID: chantierId ?? '', Chantier: chantierName,
        Materiel_ID: row.id_materiel, Matricule: row.Materiel?.matricule || '', Type: row.Materiel?.type || '',
        Chantier_Actuel: row.Materiel?.chantier_actuel || '', Meteo: row.meteo || '',
        Compteur_Prec: row.compteur_prec ?? '', Compteur_Nv: row.compteur_nv ?? '',
        Heures_Reels: row.heures_reels ?? '', Qte: row.qte ?? '',
        Gasoil: row.gasoil ?? '', Essence: row.essence ?? '',
        Graisse: row.graisse || '', Vidange: row.vidange || '',
        Reparation: row.reparation || '', Piece_De_Rechange: row.piece_de_rechange || '',
        Observation: row.observation || ''
      };
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'PointageMateriels');
    XLSX.writeFile(wb, `pointage_materiels_${startDate}_${endDate}.xlsx`);
  };

  const handleExportCSV = () => {
    const headers = ['ID','Date','Chantier','Materiel_ID','Matricule','Type','Heures','Qte',
      'Gasoil','Essence','Cmp_Prec','Cmp_Nv','Meteo','Gasoil_L','Graisse','Vidange',
      'Reparation','Piece','Observation'];
    const escape = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const dataRows = filteredPointages.map(r => [
      r.id, r.date, getChantierName(r), r.id_materiel,
      r.Materiel?.matricule || '', r.Materiel?.type || '',
      r.heures_reels ?? '', r.qte ?? '', r.gasoil ?? '', r.essence ?? '',
      r.compteur_prec ?? '', r.compteur_nv ?? '', r.meteo || '',
      r.gasoil_l ?? '', r.graisse || '', r.vidange || '',
      r.reparation || '', r.piece_de_rechange || '', r.observation || ''
    ].map(escape).join(';'));
    const csv = [headers.join(';'), ...dataRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `pointage_materiels_${startDate}_${endDate}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const handleExportPDF = () => {
    // ── Grouper par matériel ──
    const matIdList = selectedMateriels.length
      ? selectedMateriels.map(m => Number(m.value))
      : [...new Set(filteredPointages.map(p => Number(p.id_materiel)).filter(Boolean))];

    const groups = matIdList
      .map(matId => ({
        mat:  materiels.find(m => Number(m.id) === matId),
        rows: filteredPointages
          .filter(p => Number(p.id_materiel) === matId)
          .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
      }))
      .filter(g => g.rows.length > 0);

    if (!groups.length) return;

    const doc    = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageW  = doc.internal.pageSize.getWidth();   // 297
    const pageH  = doc.internal.pageSize.getHeight();  // 210
    const mg     = 10;
    const usable = pageW - mg * 2;                     // 277

    // Palette
    const C_BROWN  = [109, 78,  54];
    const C_BLIGHT = [243, 237, 230];
    const C_WHITE  = [255, 255, 255];
    const C_TEXT   = [78,  57,  41];
    const C_GRAY   = [150, 150, 150];
    const C_BLUE   = [21,  101, 192];
    const C_ORANGE = [230, 81,  0];
    const C_GREEN  = [46,  125, 50];
    const C_TEAL   = [0,   105, 92];
    const C_RED    = [211, 47,  47];
    const C_SLATE  = [69,  90,  100];

    groups.forEach(({ mat, rows }, idx) => {
      if (idx > 0) doc.addPage();

      // ── Totaux ──
      const totalHeures   = rows.reduce((s, r) => s + (Number(r.heures_reels) || 0), 0);
      const totalGasoil   = rows.reduce((s, r) => s + (Number(r.gasoil)       || 0), 0);
      const totalEssence  = rows.reduce((s, r) => s + (Number(r.essence)      || 0), 0);
      const totalGasoilL  = rows.reduce((s, r) => s + (Number(r.gasoil_l)     || 0), 0);
      const nbJours       = rows.length;
      const sortedRows    = [...rows].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
      const firstCmpPrec  = sortedRows.find(r => r.compteur_prec != null)?.compteur_prec;
      const lastCmpNv     = [...sortedRows].reverse().find(r => r.compteur_nv != null)?.compteur_nv;
      const deltaComp     = firstCmpPrec != null && lastCmpNv != null
        ? Number(lastCmpNv) - Number(firstCmpPrec) : null;
      const nbVidanges    = rows.filter(r => (r.vidange    || '').trim() !== '').length;
      const nbReparations = rows.filter(r => (r.reparation || '').trim() !== '').length;

      let y = mg;

      // ── Bande header ──
      doc.setFillColor(...C_BROWN);
      doc.rect(mg, y, usable, 14, 'F');
      doc.setTextColor(...C_WHITE);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text('SHEMS PAIE  —  Fiche de Pointage Matériel', mg + 4, y + 6);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.text(`Période : ${startDate}  →  ${endDate}`, mg + 4, y + 11.5);
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(7.5);
      doc.text(`Généré le : ${new Date().toLocaleDateString('fr-FR')}`, pageW - mg - 3, y + 11.5, { align: 'right' });
      y += 17;

      // ── Bloc info matériel ──
      doc.setFillColor(...C_BLIGHT);
      doc.rect(mg, y, usable, 11, 'F');
      doc.setDrawColor(...C_BROWN);
      doc.setLineWidth(0.4);
      doc.rect(mg, y, usable, 11, 'S');

      const matricule = mat?.matricule || `Matériel #${rows[0]?.id_materiel}`;
      const type      = mat?.type || '—';
      const chantActuel = mat?.chantier_actuel || getChantierName(rows[0]) || '—';
      const etat      = (mat?.etat || 'actif').toLowerCase();

      const etatClr   = etat === 'bloque' ? C_RED : C_GREEN;
      const etatLabel = etat === 'bloque' ? 'Bloqué' : 'En service';

      // Statut en petit à gauche en haut
      doc.setTextColor(...etatClr);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.text(`● ${etatLabel}`, mg + 4, y + 3.5);

      // Matricule dessous
      doc.setTextColor(...C_BROWN);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10.5);
      doc.text(matricule, mg + 4, y + 9);

      // Type + chantier au centre
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor(...C_TEXT);
      doc.text(`Type : ${type}`, mg + 72, y + 4.5);
      doc.text(`Chantier actuel : ${chantActuel}`, mg + 72, y + 9.5);
      y += 14;

      // ── 8 boîtes de résumé sur 2 rangées ──
      const nbCols  = 4;
      const boxGap  = 3;
      const boxW    = (usable - boxGap * (nbCols - 1)) / nbCols;
      const boxH    = 18;

      const drawSummaryBox = (bx, by, label, value, accent, fontSize = 11, value2 = null) => {
        // bande couleur en haut
        doc.setFillColor(...accent);
        doc.rect(bx, by, boxW, 3.5, 'F');
        // fond blanc
        doc.setFillColor(248, 248, 250);
        doc.rect(bx, by + 3.5, boxW, boxH - 3.5, 'F');
        // bordure
        doc.setDrawColor(220, 215, 210);
        doc.setLineWidth(0.25);
        doc.rect(bx, by, boxW, boxH, 'S');
        // label
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.setTextColor(...C_GRAY);
        doc.text(label, bx + boxW / 2, by + 8, { align: 'center' });
        // valeurs
        if (value2 !== null) {
          // deux valeurs côte à côte
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(fontSize);
          doc.setTextColor(...accent);
          doc.text(String(value),  bx + boxW * 0.28, by + 13.5, { align: 'center' });
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8);
          doc.setTextColor(...C_GRAY);
          doc.text('·', bx + boxW / 2, by + 13.5, { align: 'center' });
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(fontSize);
          doc.setTextColor(...accent);
          doc.text(String(value2), bx + boxW * 0.72, by + 13.5, { align: 'center' });
        } else {
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(fontSize);
          doc.setTextColor(...accent);
          doc.text(String(value), bx + boxW / 2, by + 13.5, { align: 'center' });
        }
      };

      const equivJours = totalHeures > 0 ? `${(totalHeures / 9).toFixed(1)} j` : null;
      const row1 = [
        { label: 'Heures  ·  Jours', value: `${totalHeures} h`, accent: C_BLUE, v2: equivJours },
        { label: 'Gasoil total',     value: `${totalGasoil} L`, accent: C_ORANGE },
        { label: 'Essence',          value: `${totalEssence} L`, accent: [121, 85, 72] },
        { label: 'Gasoil citerne',   value: `${totalGasoilL} L`, accent: C_TEAL },
      ];
      row1.forEach((b, i) => drawSummaryBox(mg + i * (boxW + boxGap), y, b.label, b.value, b.accent, 11, b.v2 ?? null));
      y += boxH + boxGap;

      const cmpLabel = deltaComp != null
        ? `${firstCmpPrec} → ${lastCmpNv}  (Δ ${deltaComp})`
        : '—';
      const row2 = [
        { label: 'Jours pointés',   value: `${nbJours}`,     accent: C_BROWN, fs: 11 },
        { label: 'Compteur (Δ)',    value: cmpLabel,          accent: C_SLATE, fs: deltaComp != null ? 7 : 11 },
        { label: 'Vidanges',        value: `${nbVidanges}`,   accent: C_GREEN, fs: 11 },
        { label: 'Réparations',     value: `${nbReparations}`,accent: C_RED,   fs: 11 },
      ];
      row2.forEach((b, i) => drawSummaryBox(mg + i * (boxW + boxGap), y, b.label, b.value, b.accent, b.fs));
      y += boxH + 4;

      // ── Tableau journalier ──
      const tableData = rows.map(r => [
        r.date || '',
        getChantierName(r) || '—',
        r.heures_reels != null ? `${r.heures_reels} h` : '—',
        r.qte != null ? String(r.qte) : '—',
        Number(r.gasoil)   > 0 ? `${r.gasoil} L`   : '—',
        Number(r.essence)  > 0 ? `${r.essence} L`  : '—',
        Number(r.gasoil_l) > 0 ? `${r.gasoil_l} L` : '—',
        r.compteur_prec != null ? String(r.compteur_prec) : '—',
        r.compteur_nv   != null ? String(r.compteur_nv)   : '—',
        r.vidange || '—',
        r.observation || '—',
      ]);

      autoTable(doc, {
        startY:  y,
        margin:  { left: mg, right: mg },
        head:    [['Date', 'Chantier', 'Heures', 'Qté', 'Gasoil', 'Essence', 'Gasoil L', 'Cmp préc', 'Cmp nv', 'Vidange', 'Observation']],
        body:    tableData,
        foot:    [['TOTAL', '', `${totalHeures} h`, '', `${totalGasoil} L`, `${totalEssence} L`, `${totalGasoilL} L`, '', '', '', '']],
        styles: {
          fontSize:    8,
          cellPadding: 2.5,
          valign:      'middle',
          textColor:   C_TEXT,
          lineColor:   [220, 210, 200],
          lineWidth:   0.2,
        },
        headStyles: {
          fillColor:  C_BROWN,
          textColor:  C_WHITE,
          fontStyle:  'bold',
          fontSize:   7.5,
          halign:     'center',
        },
        footStyles: {
          fillColor:  C_BLIGHT,
          textColor:  C_BROWN,
          fontStyle:  'bold',
          fontSize:   8,
        },
        alternateRowStyles: { fillColor: [253, 250, 247] },
        columnStyles: {
          0:  { cellWidth: 22 },
          1:  { cellWidth: 43 },
          2:  { cellWidth: 17, halign: 'right' },
          3:  { cellWidth: 13, halign: 'right' },
          4:  { cellWidth: 17, halign: 'right' },
          5:  { cellWidth: 17, halign: 'right' },
          6:  { cellWidth: 18, halign: 'right' },
          7:  { cellWidth: 20, halign: 'right' },
          8:  { cellWidth: 20, halign: 'right' },
          9:  { cellWidth: 22 },
          10: { cellWidth: 'auto' },
        },
        didParseCell: (data) => {
          if (data.section !== 'body') return;
          const v = data.cell.raw;
          if (data.column.index === 2 && v !== '—') {
            data.cell.styles.textColor = C_BLUE;
            data.cell.styles.fontStyle = 'bold';
          }
          if (data.column.index === 4 && v !== '—') {
            data.cell.styles.textColor = C_ORANGE;
            data.cell.styles.fontStyle = 'bold';
          }
          if (data.column.index === 9 && v !== '—') {
            data.cell.styles.textColor = C_GREEN;
          }
        },
      });

      // ── Numéro de page ──
      doc.setFontSize(7);
      doc.setTextColor(...C_GRAY);
      doc.setFont('helvetica', 'normal');
      doc.text(
        `Matériel ${idx + 1} / ${groups.length}  —  ${matricule}`,
        pageW - mg - 3, pageH - 4, { align: 'right' }
      );
    });

    doc.save(`pointage_materiels_${startDate}_${endDate}.pdf`);
  };

  // ── Modal (add / edit) ──
  const renderModal = (isEdit = false) => {
    const show   = isEdit ? showEditModal : showAddModal;
    const onHide = () => {
      if (isEdit) { setShowEditModal(false); setSelectedPointage(null); }
      else          setShowAddModal(false);
      resetMessages();
    };
    const title  = isEdit ? 'Modifier le pointage matériel' : 'Ajouter un pointage matériel';
    const action = isEdit ? handleUpdate : handleSubmit;

    return (
      <Modal show={show} onHide={onHide} size="xl" centered backdrop="static">
        <Modal.Header closeButton={!saving} style={{ backgroundColor: COLORS.bg, borderBottom: `1px solid ${COLORS.border}` }}>
          <Modal.Title style={{ color: COLORS.brown }}>
            {title}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ backgroundColor: COLORS.cream }}>
          {errorMsg && (
            <Alert variant="danger" className="shadow-sm" dismissible onClose={() => setErrorMsg('')}>
              {errorMsg}
            </Alert>
          )}
          {successMsg && (
            <Alert variant="success" className="shadow-sm" dismissible onClose={() => setSuccessMsg('')}>
              {successMsg}
            </Alert>
          )}

          <Form>
            <Row className="g-3">
              <Col md={6}>
                <Form.Label className="fw-bold" style={{ color: COLORS.brown }}>Matériel 🚜</Form.Label>
                <Select
                  styles={customSelectStyles}
                  options={materielOptions}
                  value={selectedFormMateriel}
                  onChange={(sel) => {
                    const m = (materiels || []).find(m => Number(m.id) === Number(sel?.value));
                    setFormData(prev => ({
                      ...prev,
                      id_materiel: sel ? sel.value : '',
                      chantier_id: m?.chantier_id || prev.chantier_id || ''
                    }));
                  }}
                  isClearable isSearchable
                  placeholder="🔎 Rechercher un matériel..."
                  noOptionsMessage={() => 'Aucun matériel trouvé'}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </Col>

              <Col md={6}>
                <Form.Label className="fw-bold" style={{ color: COLORS.brown }}>Date 📅</Form.Label>
                <Form.Control type="date" name="date" value={formData.date} onChange={handleChange} className="shadow-sm" />
                <Form.Text className="text-muted">Tu peux sélectionner ou écrire directement la date.</Form.Text>
              </Col>

              <Col md={6}>
                <Form.Label className="fw-bold" style={{ color: COLORS.brown }}>Chantier 🏗️</Form.Label>
                <Select
                  styles={customSelectStyles}
                  options={allChantierOptions}
                  value={selectedFormChantier}
                  onChange={(sel) => setFormData(prev => ({ ...prev, chantier_id: sel ? sel.value : '' }))}
                  isClearable isSearchable
                  placeholder="🔎 Rechercher un chantier..."
                  noOptionsMessage={() => 'Aucun chantier trouvé'}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
                <Form.Text className="text-muted">Prérempli automatiquement selon le matériel si disponible.</Form.Text>
              </Col>

              <Col md={6}>
                <Form.Label className="fw-bold" style={{ color: COLORS.brown }}>Météo ☁️</Form.Label>
                <Form.Control name="meteo" value={formData.meteo} onChange={handleChange} placeholder="Ex: Soleil" className="shadow-sm" />
              </Col>

              <Col md={3}>
                <Form.Label className="fw-bold" style={{ color: COLORS.brown }}>Compteur préc</Form.Label>
                <Form.Control name="compteur_prec" value={formData.compteur_prec} onChange={handleChange} placeholder="0" className="shadow-sm" />
              </Col>

              <Col md={3}>
                <Form.Label className="fw-bold" style={{ color: COLORS.brown }}>Compteur nv</Form.Label>
                <Form.Control name="compteur_nv" value={formData.compteur_nv} onChange={handleChange} placeholder="0" className="shadow-sm" />
              </Col>

              <Col md={3}>
                <Form.Label className="fw-bold" style={{ color: COLORS.brown }}>Heures réelles</Form.Label>
                <Form.Control name="heures_reels" value={formData.heures_reels} onChange={handleChange} placeholder="0" className="shadow-sm" />
              </Col>

              <Col md={3}>
                <Form.Label className="fw-bold" style={{ color: COLORS.brown }}>Qté</Form.Label>
                <Form.Control name="qte" value={formData.qte} onChange={handleChange} placeholder="0" className="shadow-sm" />
              </Col>

              <Col md={4}>
                <Form.Label className="fw-bold" style={{ color: COLORS.brown }}>Gasoil</Form.Label>
                <Form.Control name="gasoil" value={formData.gasoil} onChange={handleChange} placeholder="0" className="shadow-sm" />
              </Col>

              <Col md={4}>
                <Form.Label className="fw-bold" style={{ color: COLORS.brown }}>Essence</Form.Label>
                <Form.Control name="essence" value={formData.essence} onChange={handleChange} placeholder="0" className="shadow-sm" />
              </Col>

              <Col md={8}>
                <Form.Label className="fw-bold" style={{ color: COLORS.brown }}>Citerne 🛢️</Form.Label>
                <Select
                  styles={customSelectStyles}
                  options={citerneOptions}
                  value={selectedFormCiterne}
                  onChange={(sel) => setFormData(prev => ({ ...prev, citerne_id: sel ? sel.value : '' }))}
                  isClearable isSearchable
                  placeholder="🔎 Sélectionner une citerne..."
                  noOptionsMessage={() => 'Aucune citerne trouvée'}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
                <Form.Text className="text-muted">Obligatoire si gasoil citerne &gt; 0.</Form.Text>
              </Col>

              <Col md={4}>
                <Form.Label className="fw-bold" style={{ color: COLORS.brown }}>Gasoil citerne (L) <FaGasPump className="ms-1" /></Form.Label>
                <Form.Control name="gasoil_l" value={formData.gasoil_l} onChange={handleChange} placeholder="0" className="shadow-sm" />
                <Form.Text className="text-muted">Litres prélevés depuis la citerne.</Form.Text>
              </Col>

              <Col md={4}>
                <Form.Label className="fw-bold" style={{ color: COLORS.brown }}>Graisse</Form.Label>
                <Form.Control name="graisse" value={formData.graisse} onChange={handleChange} placeholder="Ex: OK" className="shadow-sm" />
              </Col>

              <Col md={6}>
                <Form.Label className="fw-bold" style={{ color: COLORS.brown }}>Vidange</Form.Label>
                <Form.Control name="vidange" value={formData.vidange} onChange={handleChange} placeholder="Ex: oui/non" className="shadow-sm" />
              </Col>

              <Col md={6}>
                <Form.Label className="fw-bold" style={{ color: COLORS.brown }}>Réparation</Form.Label>
                <Form.Control as="textarea" rows={2} name="reparation" value={formData.reparation} onChange={handleChange} className="shadow-sm" />
              </Col>

              <Col md={6}>
                <Form.Label className="fw-bold" style={{ color: COLORS.brown }}>Pièce de rechange</Form.Label>
                <Form.Control as="textarea" rows={2} name="piece_de_rechange" value={formData.piece_de_rechange} onChange={handleChange} className="shadow-sm" />
              </Col>

              <Col md={6}>
                <Form.Label className="fw-bold" style={{ color: COLORS.brown }}>Observation</Form.Label>
                <Form.Control as="textarea" rows={2} name="observation" value={formData.observation} onChange={handleChange} className="shadow-sm" />
              </Col>
            </Row>
          </Form>
        </Modal.Body>

        <Modal.Footer style={{ backgroundColor: COLORS.cream }}>
          <Button variant="secondary" onClick={onHide} disabled={saving}>Annuler</Button>
          <Button onClick={action} disabled={saving} style={{ backgroundColor: COLORS.brown, borderColor: COLORS.brown }}>
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  // ── Render ──
  return (
    <div className="pml-page">
      <div className="container mt-4 mb-5">

        {/* ── Header ── */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
          <div>
            <h2 className="m-0" style={{ color: '#6d4e36' }}>
              <FaTruck className="me-2" />
              Liste des pointages matériels
            </h2>
            <div className="pml-subtitle">
              {loading ? '...' : (
                <>
                  <span className="pml-subtitle-count">{filteredPointages.length}</span>
                  {' '}pointage{filteredPointages.length !== 1 ? 's' : ''}
                  {selectedChantiers.length > 0 && (
                    <span className="ms-2">
                      · {selectedChantiers.length} chantier{selectedChantiers.length !== 1 ? 's' : ''} sélectionné{selectedChantiers.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="d-flex gap-2 flex-wrap">
            <Button variant="outline-secondary" size="sm" onClick={fetchData} disabled={loading}>
              <FaSyncAlt className="me-1" /> Rafraîchir
            </Button>
            <Button variant="outline-success" size="sm" onClick={handleExportExcel} disabled={loading || filteredPointages.length === 0}>
              <FaFileExcel className="me-1" /> Excel
            </Button>
            <Button variant="outline-secondary" size="sm" onClick={handleExportCSV} disabled={loading || filteredPointages.length === 0}>
              <FaFileCsv className="me-1" /> CSV
            </Button>
            <Button variant="outline-danger" size="sm" onClick={handleExportPDF} disabled={loading || filteredPointages.length === 0}>
              <FaFilePdf className="me-1" /> PDF
            </Button>
            {canAdd && (
              <Button size="sm" style={{ backgroundColor: '#17a2b8', borderColor: '#17a2b8', color: '#fff' }}
                onClick={() => setShowTransferModal(true)}>
                <FaExchangeAlt className="me-1" /> Transfert Gasoil
              </Button>
            )}
            {canAdd && (
              <Button variant="success" size="sm" onClick={openAddModal}>
                <FaPlus className="me-1" /> Ajouter
              </Button>
            )}
          </div>
        </div>

        {/* ── Alerts ── */}
        {errorMsg && (
          <Alert variant="danger" dismissible onClose={() => setErrorMsg('')}>{errorMsg}</Alert>
        )}
        {successMsg && (
          <Alert variant="success" dismissible onClose={() => setSuccessMsg('')}>{successMsg}</Alert>
        )}

        {/* ── KPI Cards ── */}
        <Row className="g-3 mb-3">
          <Col xs={6} md={3}>
            <KpiCard title="Total pointages" value={kpi.total}           color="#6d4e36" icon={FaBoxes}    />
          </Col>
          <Col xs={6} md={3}>
            <KpiCard title="Heures totales"  value={kpi.totalHeures}     color="#1565c0" icon={FaClock}    />
          </Col>
          <Col xs={6} md={3}>
            <KpiCard title="Gasoil total"    value={`${kpi.totalGasoil} L`} color="#e65100" icon={FaGasPump} />
          </Col>
          <Col xs={6} md={3}>
            <KpiCard title="Chantiers"       value={kpi.chantiersCount || 'Tous'} color="#2e7d32" icon={FaBuilding} />
          </Col>
        </Row>

        {/* ── Hero Search ── */}
        <div className="pml-search-hero mb-3">
          <InputGroup>
            <InputGroup.Text className="pml-search-icon"><FaSearch /></InputGroup.Text>
            <Form.Control
              className="pml-search-input"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              placeholder="Rechercher par matricule, chantier, type, observation, date..."
              autoComplete="off"
            />
            {searchText && (
              <Button variant="outline-secondary" onClick={() => setSearchText('')}>×</Button>
            )}
          </InputGroup>
        </div>

        {/* ── Filters ── */}
        <Card className="pml-filter-card mb-3">
          <Card.Body className="py-2">
            <Row className="g-2 align-items-end">
              <Col md={2}>
                <Form.Label className="small fw-bold mb-1">
                  <FaCalendarAlt className="me-1" /> Date début
                </Form.Label>
                <Form.Control type="date" value={startDate} size="sm"
                  onChange={e => { setStartDate(e.target.value); setPage(1); }} />
              </Col>
              <Col md={2}>
                <Form.Label className="small fw-bold mb-1">
                  <FaCalendarAlt className="me-1" /> Date fin
                </Form.Label>
                <Form.Control type="date" value={endDate} size="sm"
                  onChange={e => { setEndDate(e.target.value); setPage(1); }} />
              </Col>
              <Col md={3}>
                <Form.Label className="small fw-bold mb-1">
                  <FaBuilding className="me-1" /> Chantiers
                </Form.Label>
                <Select
                  isMulti isClearable isSearchable
                  classNamePrefix="react-select"
                  className="op-select"
                  options={chantierOptions}
                  value={selectedChantiers}
                  onChange={val => { setSelectedChantiers(val || []); setPage(1); }}
                  placeholder="Tous les chantiers..."
                  noOptionsMessage={() => 'Aucun chantier trouvé'}
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                />
              </Col>
              <Col md={3}>
                <Form.Label className="small fw-bold mb-1">
                  <FaTruck className="me-1" /> Matériels
                </Form.Label>
                <Select
                  isMulti isClearable isSearchable
                  classNamePrefix="react-select"
                  className="op-select"
                  options={filterMaterielOptions}
                  value={selectedMateriels}
                  onChange={val => { setSelectedMateriels(val || []); setPage(1); }}
                  placeholder="Tous les matériels..."
                  noOptionsMessage={() => 'Aucun matériel trouvé'}
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                />
              </Col>
              <Col md={2} className="d-flex align-items-end gap-2 justify-content-end flex-wrap">
                <Button variant="outline-secondary" size="sm"
                  onClick={() => { setSelectedChantiers([]); setSelectedMateriels([]); setSearchText(''); setPage(1); }}>
                  <FaBroom className="me-1" /> Réinitialiser
                </Button>
                <span className="pml-result-badge">
                  {filteredPointages.length} ligne{filteredPointages.length !== 1 ? 's' : ''}
                </span>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* ── Table ── */}
        {loading ? (
          <div className="d-flex align-items-center gap-2 mt-3">
            <Spinner animation="border" />
            <span>Chargement des pointages matériels...</span>
          </div>
        ) : (
          <div className="pml-table-wrap">
            <table>
              <thead>
                <tr>
                  <TH noSort style={{ width: 110, textAlign: 'center' }}>Actions</TH>
                  <TH field="id"          style={{ width: 60,  textAlign: 'center' }}>#</TH>
                  <TH field="date"        style={{ minWidth: 105 }}>Date</TH>
                  <TH field="chantier"    style={{ minWidth: 160 }}>Chantier</TH>
                  <TH field="matricule"   style={{ minWidth: 145 }}>Matricule</TH>
                  <TH field="type"        style={{ minWidth: 130 }}>Type</TH>
                  <TH field="heures_reels" style={{ width: 85,  textAlign: 'right' }}>Heures</TH>
                  <TH field="qte"         style={{ width: 65,  textAlign: 'right' }}>Qté</TH>
                  <TH field="gasoil"      style={{ width: 95,  textAlign: 'right' }}>Gasoil</TH>
                  <TH field="essence"     style={{ width: 95,  textAlign: 'right' }}>Essence</TH>
                  <TH noSort              style={{ minWidth: 200 }}>Observation</TH>
                </tr>
              </thead>
              <tbody>
                {pagedPointages.length === 0 ? (
                  <tr>
                    <td colSpan={11}>
                      <div className="pml-empty-state">
                        <FaTruck className="pml-empty-icon" />
                        <div>Aucun pointage matériel trouvé</div>
                      </div>
                    </td>
                  </tr>
                ) : pagedPointages.map((row, i) => {
                  const isBloque   = (row.Materiel?.etat || '').toLowerCase().trim() === 'bloque';
                  const chantierName = getChantierName(row);
                  const gasoilVal  = Number(row.gasoil  || 0);
                  const essenceVal = Number(row.essence || 0);

                  return (
                    <tr
                      key={row.id}
                      className={isBloque ? 'pml-row-bloque' : ''}
                      style={{ background: i % 2 === 0 ? '#fff' : '#fdfaf7' }}
                    >
                      {/* Actions */}
                      <td style={{ textAlign: 'center' }}>
                        <div className="d-flex gap-1 justify-content-center align-items-center">
                          {user?.role !== 'pointeur' && canAccess(user, 'pointage-materiels:write') && (
                            <Button size="sm" variant="warning" className="pml-action-btn" title="Modifier"
                              onClick={() => openEditModal(row)}>
                              <FaEdit />
                            </Button>
                          )}
                          {user?.role !== 'pointeur' && canAccess(user, 'pointage-materiels:delete') && (
                            <Button size="sm" variant="danger" className="pml-action-btn" title="Supprimer"
                              onClick={() => handleDelete(row.id)}>
                              <FaTrashAlt />
                            </Button>
                          )}
                          <Dropdown align="end">
                            <Dropdown.Toggle as={Button} size="sm" variant="outline-secondary"
                              className="pml-action-btn pml-more-btn" id={`drop-${row.id}`} title="Détails">
                              <FaEllipsisV />
                            </Dropdown.Toggle>
                            <Dropdown.Menu className="pml-action-menu">
                              <span className="pml-detail-label">Compteurs</span>
                              <Dropdown.ItemText>
                                Préc: <strong>{row.compteur_prec ?? '—'}</strong> → Nv: <strong>{row.compteur_nv ?? '—'}</strong>
                              </Dropdown.ItemText>

                              <Dropdown.Divider />
                              <span className="pml-detail-label">Citerne gasoil</span>
                              <Dropdown.ItemText>
                                {Number(row.gasoil_l || 0) > 0 ? `${row.gasoil_l} L prélevés` : '—'}
                              </Dropdown.ItemText>

                              <Dropdown.Divider />
                              <span className="pml-detail-label">Entretien</span>
                              <Dropdown.ItemText>
                                <FaWrench className="me-1 text-muted" style={{ fontSize: '0.75rem' }} />
                                Graisse: <strong>{row.graisse || '—'}</strong> · Vidange: <strong>{row.vidange || '—'}</strong>
                              </Dropdown.ItemText>
                              {(row.reparation || row.piece_de_rechange) && (
                                <Dropdown.ItemText style={{ whiteSpace: 'normal', maxWidth: 240 }}>
                                  Répa: <strong>{row.reparation || '—'}</strong><br />
                                  Pièce: <strong>{row.piece_de_rechange || '—'}</strong>
                                </Dropdown.ItemText>
                              )}

                              {row.meteo && (
                                <>
                                  <Dropdown.Divider />
                                  <Dropdown.ItemText>☁️ Météo: <strong>{row.meteo}</strong></Dropdown.ItemText>
                                </>
                              )}

                              {row.Materiel?.chantier_actuel && (
                                <>
                                  <Dropdown.Divider />
                                  <Dropdown.ItemText>
                                    <FaBuilding className="me-1 text-muted" style={{ fontSize: '0.75rem' }} />
                                    Actuel: <strong>{row.Materiel.chantier_actuel}</strong>
                                  </Dropdown.ItemText>
                                </>
                              )}
                            </Dropdown.Menu>
                          </Dropdown>
                        </div>
                      </td>

                      <td style={{ textAlign: 'center', color: '#bbb', fontSize: '0.78rem' }}>{row.id}</td>
                      <td>{row.date}</td>
                      <td>
                        {chantierName
                          ? <span className="pml-chantier-chip">{chantierName}</span>
                          : <span className="text-muted">—</span>}
                      </td>
                      <td>
                        <span className="pml-matricule-text">{row.Materiel?.matricule || '—'}</span>
                        {isBloque && <Badge bg="danger" className="ms-2" style={{ fontSize: '0.62rem' }}>Bloqué</Badge>}
                      </td>
                      <td>{row.Materiel?.type || '—'}</td>
                      <td style={{ textAlign: 'right' }}>
                        {row.heures_reels != null
                          ? <span className="pml-hours-value">{row.heures_reels}</span>
                          : <span className="text-muted">—</span>}
                      </td>
                      <td style={{ textAlign: 'right' }}>{row.qte ?? <span className="text-muted">—</span>}</td>
                      <td style={{ textAlign: 'right' }}>
                        {gasoilVal > 0
                          ? <span className="pml-fuel-positive">{gasoilVal} L</span>
                          : <span className="text-muted">—</span>}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {essenceVal > 0
                          ? <span className="pml-fuel-positive">{essenceVal} L</span>
                          : <span className="text-muted">—</span>}
                      </td>
                      <td>
                        {row.observation
                          ? <span className="pml-obs-text" title={row.observation}>{row.observation}</span>
                          : <span className="text-muted">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div className="pml-pagination-bar">
                <button className="pml-pag-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹</button>
                <span className="pml-pag-info">
                  Page <strong>{page}</strong> / {totalPages} · {sortedPointages.length} ligne{sortedPointages.length !== 1 ? 's' : ''}
                </span>
                <button className="pml-pag-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>›</button>
              </div>
            )}
          </div>
        )}
      </div>

      {renderModal(false)}
      {renderModal(true)}

      <TransfertGasoilModal
        show={showTransferModal}
        onHide={() => setShowTransferModal(false)}
        sourceCiternes={citernes}
        onSuccess={fetchData}
      />
    </div>
  );
};

export default PointageMaterielListePage;
